import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import Logo from "./Logo";

export default function Navbar() {
  const { user, logout, isQRSession } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
    setOpen(false);
  };

  return (
    <nav className="bg-farm-green dark:bg-gray-900 shadow-md
                    transition-colors duration-200">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center
                      justify-between">

        {/* Logo — links to the main marketing website */}
        <a
          href="https://www.cedarforkfarm.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpen(false)}
          aria-label="Go to Cedar Fork Farm website"
        >
          <Logo className="h-12" />
        </a>

        <div className="flex items-center gap-3">
          {/* Dark mode toggle */}
          <DarkModeToggle dark={dark} toggle={toggle} />

          {/* Hamburger (mobile) */}
          <button
            className="text-white md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {open ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-4">
            <NavLinks
              user={user}
              isQRSession={isQRSession}
              onLogout={handleLogout}
            />
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-farm-green dark:bg-gray-900
                        border-t border-green-700 dark:border-gray-700
                        px-4 pb-4 flex flex-col gap-2">
          <NavLinks
            user={user}
            isQRSession={isQRSession}
            onLogout={handleLogout}
            onClick={() => setOpen(false)}
          />
        </div>
      )}
    </nav>
  );
}

// ── Dark mode toggle ────────────────────────────────────────────────────────
function DarkModeToggle({ dark, toggle }) {
  return (
    <button
      onClick={toggle}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      title={dark ? "Switch to light mode" : "Switch to dark mode"}
      className="relative inline-flex items-center w-12 h-6 rounded-full
                 transition-colors duration-300 focus:outline-none
                 focus:ring-2 focus:ring-white focus:ring-offset-2
                 focus:ring-offset-farm-green bg-white/20 hover:bg-white/30"
    >
      {/* Track fill */}
      <span
        className={`absolute inset-0 rounded-full transition-colors
                    duration-300 ${dark ? "bg-indigo-500" : "bg-white/20"}`}
      />
      {/* Thumb */}
      <span
        className={`relative z-10 inline-flex items-center justify-center
                    w-5 h-5 rounded-full shadow transition-transform
                    duration-300 transform text-sm
                    ${dark
                      ? "translate-x-6 bg-gray-900"
                      : "translate-x-0.5 bg-white"
                    }`}
      >
        {dark ? "🌙" : "☀️"}
      </span>
    </button>
  );
}

// ── Navigation links ────────────────────────────────────────────────────────
function NavLinks({ user, isQRSession, onLogout, onClick }) {
  const linkCls =
    "text-farm-cream hover:text-farm-tan transition-colors font-medium";

  return (
    <>
      <Link to="/" className={linkCls} onClick={onClick}>
        Home
      </Link>

      {/* Products — visible for all logged-in users, including QR sessions */}
      {user && (
        <Link to="/products" className={linkCls} onClick={onClick}>
          Products
        </Link>
      )}

      {/* Full-session-only links */}
      {user && !isQRSession && (
        <>
          <Link to="/account" className={linkCls} onClick={onClick}>
            Account
          </Link>
          <Link to="/add-credits" className={linkCls} onClick={onClick}>
            Add Credits
          </Link>
          {user.isAdmin && (
            <Link
              to="/admin"
              className="text-farm-tan hover:text-yellow-300
                         transition-colors font-semibold"
              onClick={onClick}
            >
              Admin
            </Link>
          )}
        </>
      )}

      {!user ? (
        <>
          <Link to="/login" className={linkCls} onClick={onClick}>
            Login
          </Link>
          <Link
            to="/register"
            className="bg-farm-tan text-white px-3 py-1 rounded-lg
                       hover:bg-yellow-600 transition-colors"
            onClick={onClick}
          >
            Sign Up
          </Link>
        </>
      ) : (
        <button
          onClick={onLogout}
          className="text-farm-cream hover:text-red-300
                     transition-colors font-medium"
        >
          Logout
        </button>
      )}
    </>
  );
}
